import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// ============================================
// Simple In-Memory Cache (reduces API calls)
// ============================================
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCached = (key) => {
  const item = cache.get(key);
  if (!item) return null;
  if (Date.now() > item.expiry) {
    cache.delete(key);
    return null;
  }
  return item.data;
};

const setCache = (key, data, ttl = CACHE_TTL) => {
  cache.set(key, { data, expiry: Date.now() + ttl });
};

const invalidateCache = (pattern) => {
  // Invalidate all keys matching pattern
  for (const key of cache.keys()) {
    if (key.includes(pattern)) cache.delete(key);
  }
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate axios instance for Super Admin (uses superAdminToken)
const superAdminAxios = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

superAdminAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('superAdminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

superAdminAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginAttempt = error.config?.url?.includes('/super-admin/auth/login');
      if (!isLoginAttempt) {
        localStorage.removeItem('superAdminToken');
        localStorage.removeItem('superAdminUser');
        window.location.href = '/super-admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    // Don't add token to login requests
    const isLoginRequest = config.url?.includes('/auth/login') ||
      config.url?.includes('/patient-portal/login');

    if (isLoginRequest) {
      return config;
    }

    // Check for all token types
    const patientToken = localStorage.getItem('patientToken');
    const staffToken = localStorage.getItem('token');
    const contractorToken = localStorage.getItem('contractorToken');

    // Determine which token to use based on current context
    // Check current path to determine user type
    const currentPath = window.location.pathname;

    // Use patient token for patient portal routes
    if (currentPath.startsWith('/patient') ||
      (config.url?.includes('/patient-portal/') && !config.url?.includes('/credentials/') && !config.url?.includes('/access-token/'))) {
      if (patientToken) {
        config.headers.Authorization = `Bearer ${patientToken}`;
      }
    }
    // Use contractor token for contractor portal
    else if (currentPath.startsWith('/contractor')) {
      if (contractorToken) {
        config.headers.Authorization = `Bearer ${contractorToken}`;
      }
    }
    // Use staff token for everything else (admin panel)
    else if (staffToken) {
      config.headers.Authorization = `Bearer ${staffToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 402) {
      // 402 has two distinct meanings in this app:
      //   1. code === 'ACCOUNT_NOT_ACTIVE' → tenant hasn't finished onboarding
      //      checkout (pending_payment / pending_approval / rejected). Send
      //      them into the onboarding flow — it resumes at the right step.
      //   2. No code → the paid subscription expired mid-use. Send to the
      //      expiry landing page.
      // Either way, guard against infinite redirect loops: if we're already on
      // the destination page, let the caller handle the error.
      const body = error.response?.data || {};
      const here = window.location.pathname;
      const isAccountNotActive = body.code === 'ACCOUNT_NOT_ACTIVE'
        || body.data?.code === 'ACCOUNT_NOT_ACTIVE';
      const target = isAccountNotActive ? '/onboarding' : '/subscription-expired';
      if (here !== target && !here.startsWith(target)) {
        window.location.href = target;
      }
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      // Don't redirect for login attempts or staff-initiated patient-portal calls
      const requestUrl = error.config?.url || '';
      const isLoginAttempt = requestUrl.includes('/auth/login') || requestUrl.includes('/patient-portal/login');
      const isStaffPatientCall = requestUrl.includes('/patient-portal/access-token/') || requestUrl.includes('/patient-portal/credentials/');

      if (!isLoginAttempt && !isStaffPatientCall) {
        // Clear only the relevant token based on current path
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/patient')) {
          localStorage.removeItem('patientToken');
          localStorage.removeItem('patientData');
        } else if (currentPath.startsWith('/contractor')) {
          localStorage.removeItem('contractorToken');
          localStorage.removeItem('contractorUser');
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }

        // Redirect based on current path
        if (currentPath.startsWith('/contractor')) {
          window.location.href = '/contractor/login';
        } else if (currentPath.startsWith('/patient')) {
          window.location.href = '/login';
        } else {
          window.location.href = '/staff-login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getProfile: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Customers API
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  search: (query) => api.get('/customers/search', { params: { q: query } }),
};

// Bookings API (with caching for getById)
export const bookingsAPI = {
  getAll: (params) => api.get('/bookings', { params }),
  export: (params) => api.get('/bookings/export', { params }),

  // Cached: booking details are reused for Invoice, WorkRequest, etc.
  getById: async (id) => {
    if (!id || id === 'undefined' || id === 'null') return Promise.reject(new Error('Invalid booking id'));

    const cacheKey = `booking_${id}`;
    const cached = getCached(cacheKey);
    if (cached) {
      console.log(`[Cache HIT] booking ${id}`);
      return cached;
    }

    const response = await api.get(`/bookings/${id}`);
    setCache(cacheKey, response);
    return response;
  },

  create: async (data) => {
    const response = await api.post('/bookings', data);
    invalidateCache('booking_'); // Invalidate all booking caches
    return response;
  },

  update: async (id, data) => {
    if (!id || id === 'undefined' || id === 'null') return Promise.reject(new Error('Invalid booking id'));
    const response = await api.put(`/bookings/${id}`, data);
    invalidateCache(`booking_${id}`); // Invalidate this booking's cache
    return response;
  },

  updateStatus: async (id, data) => {
    if (!id || id === 'undefined' || id === 'null') return Promise.reject(new Error('Invalid booking id'));
    const response = await api.patch(`/bookings/${id}/status`, data);
    invalidateCache(`booking_${id}`); // Invalidate this booking's cache
    return response;
  },

  updateTestsStatus: async (id, data) => {
    if (!id || id === 'undefined' || id === 'null') return Promise.reject(new Error('Invalid booking id'));
    const response = await api.patch(`/bookings/${id}/tests-status`, data);
    invalidateCache(`booking_${id}`);
    return response;
  },

  cancel: async (id, data) => {
    if (!id || id === 'undefined' || id === 'null') return Promise.reject(new Error('Invalid booking id'));
    const response = await api.post(`/bookings/${id}/cancel`, data);
    invalidateCache(`booking_${id}`);
    return response;
  },

  getByBarcode: (barcode) => api.get(`/bookings/barcode/${barcode}`),
  getPatientAccessToken: (bookingId) => api.get(`/bookings/${bookingId}/patient-access-token`),

  // Force refresh (bypass cache)
  refreshById: async (id) => {
    if (!id || id === 'undefined' || id === 'null') return Promise.reject(new Error('Invalid booking id'));
    invalidateCache(`booking_${id}`);
    const response = await api.get(`/bookings/${id}`);
    setCache(`booking_${id}`, response);
    return response;
  },
};

// Lab Tests API (with smart caching)
export const labTestsAPI = {
  // Cached: paginated list (basic info only)
  getAll: async (params) => {
    const cacheKey = `labtests_${JSON.stringify(params || {})}`;
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('[Cache HIT] lab tests page');
      return cached;
    }
    const response = await api.get('/lab-tests', { params });
    setCache(cacheKey, response, 10 * 60 * 1000); // 10 min cache
    return response;
  },

  // NEW: Get all tests WITH full structure (sections, components, ranges) - single API call
  // Perfect for admin pages where all details are needed upfront
  getAllWithFullStructure: async (params) => {
    const cacheKey = `labtests_full_${JSON.stringify(params || {})}`;
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('[Cache HIT] lab tests full list');
      return cached;
    }
    const response = await api.get('/lab-tests/full-list', { params });
    setCache(cacheKey, response, 10 * 60 * 1000); // 10 min cache
    return response;
  },

  getById: (id) => api.get(`/lab-tests/${id}`),

  // Search tests by name (for sub-test selection dropdown)
  searchByName: (params) => api.get('/lab-tests/search', { params }),
  // Culture master lists
  getCultureOrganisms: (params) => api.get('/lab-tests/culture/organisms', { params }),
  createCultureOrganism: (data) => api.post('/lab-tests/culture/organisms', data),
  updateCultureOrganism: (id, data) => api.put(`/lab-tests/culture/organisms/${id}`, data),
  setCultureOrganismStatus: (id, is_active) => api.patch(`/lab-tests/culture/organisms/${id}/status`, { is_active }),
  bulkCultureOrganismStatus: (ids, is_active) => api.post('/lab-tests/culture/organisms/bulk-status', { ids, is_active }),
  importCultureOrganisms: (formData) => api.post('/lab-tests/culture/organisms/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadCultureOrganismTemplate: () => api.get('/lab-tests/culture/organisms/template', { responseType: 'blob' }),
  getCultureAntibiotics: (params) => api.get('/lab-tests/culture/antibiotics', { params }),
  createCultureAntibiotic: (data) => api.post('/lab-tests/culture/antibiotics', data),
  updateCultureAntibiotic: (id, data) => api.put(`/lab-tests/culture/antibiotics/${id}`, data),
  setCultureAntibioticStatus: (id, is_active) => api.patch(`/lab-tests/culture/antibiotics/${id}/status`, { is_active }),
  bulkCultureAntibioticStatus: (ids, is_active) => api.post('/lab-tests/culture/antibiotics/bulk-status', { ids, is_active }),
  importCultureAntibiotics: (formData) => api.post('/lab-tests/culture/antibiotics/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadCultureAntibioticTemplate: () => api.get('/lab-tests/culture/antibiotics/template', { responseType: 'blob' }),

  // Cached: full structure for single test (fallback)
  getFullStructure: async (id) => {
    const cacheKey = `labtest_full_${id}`;
    const cached = getCached(cacheKey);
    if (cached) {
      console.log(`[Cache HIT] lab test full structure ${id}`);
      return cached;
    }
    const response = await api.get(`/lab-tests/${id}/full`);
    setCache(cacheKey, response, 10 * 60 * 1000); // 10 min cache
    return response;
  },

  create: async (data) => {
    const response = await api.post('/lab-tests', data);
    invalidateCache('labtests_'); // Invalidate list cache
    return response;
  },

  update: async (id, data) => {
    const response = await api.put(`/lab-tests/${id}`, data);
    invalidateCache('labtests_'); // Invalidate list cache
    invalidateCache(`labtest_full_${id}`); // Invalidate this test's full cache
    return response;
  },

  delete: async (id) => {
    const response = await api.delete(`/lab-tests/${id}`);
    invalidateCache('labtests_');
    invalidateCache(`labtest_full_${id}`);
    return response;
  },
  // Template import
  getTemplatesList: () => api.get('/lab-tests/templates/list'),
  getTemplateTests: (templateId, params) => api.get(`/lab-tests/templates/${templateId}/tests`, { params }),
  importFromTemplate: (data) => api.post('/lab-tests/templates/import', data),
  // Sections
  getSections: (testId) => api.get(`/lab-tests/${testId}/sections`),
  createSection: (testId, data) => api.post(`/lab-tests/${testId}/sections`, data),
  updateSection: (sectionId, data) => api.put(`/lab-tests/sections/${sectionId}`, data),
  deleteSection: (sectionId) => api.delete(`/lab-tests/sections/${sectionId}`),
  // Components
  getComponents: (sectionId) => api.get(`/lab-tests/sections/${sectionId}/components`),
  createComponent: (sectionId, data) => api.post(`/lab-tests/sections/${sectionId}/components`, data),
  updateComponent: (componentId, data) => api.put(`/lab-tests/components/${componentId}`, data),
  deleteComponent: (componentId) => api.delete(`/lab-tests/components/${componentId}`),
  // Reference Ranges
  getReferenceRanges: (componentId) => api.get(`/lab-tests/components/${componentId}/reference-ranges`),
  createReferenceRange: (componentId, data) => api.post(`/lab-tests/components/${componentId}/reference-ranges`, data),
  updateReferenceRange: (rangeId, data) => api.put(`/lab-tests/reference-ranges/${rangeId}`, data),
  deleteReferenceRange: (rangeId) => api.delete(`/lab-tests/reference-ranges/${rangeId}`),
};

// Test Categories API
export const categoriesAPI = {
  getAll: (params) => api.get('/test-categories', { params }),
  getById: (id) => api.get(`/test-categories/${id}`),
  create: (data) => api.post('/test-categories', data),
  update: (id, data) => api.put(`/test-categories/${id}`, data),
  delete: (id) => api.delete(`/test-categories/${id}`),
};

// Packages API (with caching for getAll)
export const packagesAPI = {
  getAll: async (params) => {
    const cacheKey = `packages_${JSON.stringify(params || {})}`;
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('[Cache HIT] packages');
      return cached;
    }
    const response = await api.get('/packages', { params });
    setCache(cacheKey, response, 10 * 60 * 1000); // 10 min cache
    return response;
  },
  getById: (id) => api.get(`/packages/${id}`),
  create: async (data) => {
    const response = await api.post('/packages', data);
    invalidateCache('packages_');
    return response;
  },
  update: async (id, data) => {
    const response = await api.put(`/packages/${id}`, data);
    invalidateCache('packages_');
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/packages/${id}`);
    invalidateCache('packages_');
    return response;
  },
};

// Branches API
export const branchesAPI = {
  getAll: (params) => api.get('/branches', { params }),
  getById: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
};

// Lab Results API
export const labResultsAPI = {
  getAll: (params) => api.get('/lab-results', { params }),
  getDetails: (bookingTestId) => api.get(`/lab-results/${bookingTestId}`),
  getBookingResults: (bookingId) => api.get(`/lab-results/booking/${bookingId}/view`),
  enterResult: (bookingTestId, data) => api.post(`/lab-results/${bookingTestId}`, data),
  approve: (bookingIds) => api.post('/lab-results/approve', { booking_ids: bookingIds }),
  release: (bookingIds) => api.post('/lab-results/release', { booking_ids: bookingIds }),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  updateStatus: (id, is_active) => api.patch(`/users/${id}/status`, { is_active }),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
};

// Roles API
export const rolesAPI = {
  getAll: () => api.get('/roles'),
  getAllPages: () => api.get('/roles/pages'),
  getPermissions: (roleId) => api.get(`/roles/${roleId}/permissions`),
  updatePermissions: (roleId, allowedPages) => api.put(`/roles/${roleId}/permissions`, { allowed_pages: allowedPages }),
  getMyPermissions: () => api.get('/roles/my-permissions'),
  create: (data) => api.post('/roles', data),
  update: (roleId, data) => api.put(`/roles/${roleId}`, data),
  delete: (roleId) => api.delete(`/roles/${roleId}`),
};

// Reports API
export const reportsAPI = {
  getMainDashboard: () => api.get('/reports/main-dashboard'),
  getIncomeReport: (params) => api.get('/reports/income', { params }),
  getDailyIncomeReport: (params) => api.get('/reports/daily', { params }),
  getSalesAnalysis: (params) => api.get('/reports/sales', { params }),
};

// Devices API
export const devicesAPI = {
  getAll: (params) => api.get('/devices', { params }),
  getById: (id) => api.get(`/devices/${id}`),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  delete: (id) => api.delete(`/devices/${id}`),
  // Analyzer Mappings
  getMappings: (params) => api.get('/devices/mappings/all', { params }),
  createMapping: (data) => api.post('/devices/mappings', data),
  updateMapping: (id, data) => api.put(`/devices/mappings/${id}`, data),
  deleteMapping: (id) => api.delete(`/devices/mappings/${id}`),
  getLabTestComponents: (labTestId) => api.get(`/devices/lab-test/${labTestId}/components`),
  createBulkMappings: (data) => api.post('/devices/mappings/bulk', data),
  // API Key
  getApiKey: () => api.get('/devices/api-key/current'),
  generateApiKey: () => api.post('/devices/api-key/generate'),
};

// Marketing / Coupons API
export const couponsAPI = {
  getAll: () => api.get('/marketing/coupons'),
  getById: (id) => api.get(`/marketing/coupons/${id}`),
  getByCode: (code) => api.get(`/marketing/coupons/code/${code}`),
  create: (data) => api.post('/marketing/coupons', data),
  update: (id, data) => api.put(`/marketing/coupons/${id}`, data),
  delete: (id) => api.delete(`/marketing/coupons/${id}`),
};

// WhatsApp Marketing API
export const whatsappMarketingAPI = {
  sendCampaign: (data) => api.post('/marketing/whatsapp-campaigns/send', data),
  getAllCustomers: () => api.get('/customers', { params: { limit: 1000 } }),
};

// Settings API
export const settingsAPI = {
  getWhatsappSettings: () => api.get('/settings/whatsapp'),
  updateWhatsappSettings: (data) => api.put('/settings/whatsapp', data),
  getSmsSettings: () => api.get('/settings/sms'),
  updateSmsSettings: (data) => api.put('/settings/sms', data),
  testSms: (data) => api.post('/notifications/test-sms', data),
  smsBalance: () => api.get('/notifications/sms-balance'),
  getLabSettings: () => api.get('/settings/lab'),
  updateLabSettings: (data) => api.put('/settings/lab', data),
  updateLogos: (data) => api.put('/settings/logos', data),
};

// Tenant API
export const tenantAPI = {
  getMyTenant: () => api.get('/tenants/me'),
};

// ZATCA Integration API (supports per-branch via branch_id)
export const zatcaAPI = {
  getStatus: (branchId) => api.get('/tenants/zatca/status', { params: branchId ? { branch_id: branchId } : {} }),
  saveProfile: (data) => api.post('/tenants/zatca/profile', data),
  generateCsr: (branchId) => api.post('/tenants/zatca/generate-csr', branchId ? { branch_id: branchId } : {}),
  requestCompliance: (otp, branchId) => api.post('/tenants/zatca/compliance', { otp, ...(branchId ? { branch_id: branchId } : {}) }),
  runComplianceChecks: (branchId) => api.post('/tenants/zatca/compliance-checks', branchId ? { branch_id: branchId } : {}),
  requestProduction: (branchId) => api.post('/tenants/zatca/production', branchId ? { branch_id: branchId } : {}),
  reset: (branchId) => api.post('/tenants/zatca/reset', branchId ? { branch_id: branchId } : {}),
  getLogs: (params) => api.get('/tenants/zatca/logs', { params }),
};

// Patient Portal API
export const patientPortalAPI = {
  // Auth
  login: (credentials) => api.post('/patient-portal/login', credentials),
  loginWithPhone: (data) => api.post('/patient-portal/login-phone', data),
  loginWithQR: (accessToken) => api.post('/patient-portal/login-qr', { access_token: accessToken }),
  sendOTP: (data) => api.post('/patient-portal/send-otp', data),
  verifyOTP: (data) => api.post('/patient-portal/verify-otp', data),
  getAccessToken: (customerId) => api.get(`/patient-portal/access-token/${customerId}`),

  // Patient data (requires patient token)
  getProfile: () => api.get('/patient-portal/me'),
  getBookings: () => api.get('/patient-portal/bookings'),
  getBookingResults: (bookingId) => api.get(`/patient-portal/bookings/${bookingId}/results`),
  generateAIAnalysis: (bookingId) => api.post(`/patient-portal/bookings/${bookingId}/ai-analysis`),
  getBookingReport: (bookingId) => api.get(`/patient-portal/bookings/${bookingId}/report`),

  // Staff functions
  getCredentialsForInvoice: (customerId) => api.get(`/patient-portal/credentials/${customerId}`),
  markPasswordViewed: (customerId) => api.post(`/patient-portal/credentials/${customerId}/viewed`),
  resetPassword: (customerId) => api.post(`/patient-portal/credentials/${customerId}/reset`),
};

// Contractor Labs API
export const contractorLabsAPI = {
  // Labs management
  getAll: (params) => api.get('/contractor-labs', { params }),
  getById: (id) => api.get(`/contractor-labs/${id}`),
  create: (data) => api.post('/contractor-labs', data),
  update: (id, data) => api.put(`/contractor-labs/${id}`, data),
  delete: (id) => api.delete(`/contractor-labs/${id}`),

  // Prices
  getPrices: (labId) => api.get(`/contractor-labs/${labId}/prices`),
  updatePrices: (labId, data) => api.put(`/contractor-labs/${labId}/prices`, data),

  // Bookings (admin)
  getAllBookings: (params) => api.get('/contractor-labs/bookings/all', { params }),
  getBookingById: (id) => api.get(`/contractor-labs/bookings/${id}`),
  createBooking: (data) => api.post('/contractor-labs/bookings', data),
  updateBookingStatus: (id, data) => api.put(`/contractor-labs/bookings/${id}/status`, data),
  deleteBooking: (id) => api.delete(`/contractor-labs/bookings/${id}`),

  // Bookings (contractor portal)
  getMyBookings: (params) => api.get('/contractor-labs/bookings/my', { params }),

  // Accounts: contractor side
  getMyStatement: (params) => api.get('/contractor-labs/my-statement', { params }),
  getMyPayments: () => api.get('/contractor-labs/my-payments'),
  submitMyPayment: (formData) => api.post('/contractor-labs/my-payments', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  // Accounts: admin side
  getAccountsSummary: () => api.get('/contractor-labs/accounts/summary'),
  getLabStatement: (labId, params) => api.get(`/contractor-labs/${labId}/statement`, { params }),
  getLabPayments: (labId, params) => api.get(`/contractor-labs/${labId}/payments`, { params }),
  confirmPayment: (paymentId, data) => api.patch(`/contractor-labs/payments/${paymentId}/confirm`, data),
  rejectPayment: (paymentId, data) => api.patch(`/contractor-labs/payments/${paymentId}/reject`, data),
};

// Quality Control (QC) API
export const qcAPI = {
  // Controls
  getControls: (params) => api.get('/qc/controls', { params }),
  getActiveControls: () => api.get('/qc/controls/active'),
  getControlById: (id) => api.get(`/qc/controls/${id}`),
  createControl: (data) => api.post('/qc/controls', data),
  updateControl: (id, data) => api.put(`/qc/controls/${id}`, data),
  deleteControl: (id) => api.delete(`/qc/controls/${id}`),

  // Daily runs
  submitRun: (data) => api.post('/qc/runs', data),
  getRuns: (params) => api.get('/qc/runs', { params }),

  // Dashboard
  getDashboard: (params) => api.get('/qc/dashboard', { params }),
};

// Results Analysis API
export const resultsAnalysisAPI = {
  getOverview: (params) => api.get('/results-analysis/overview', { params }),
  getCriticalPatients: (params) => api.get('/results-analysis/critical-patients', { params }),
  getFollowUp: (params) => api.get('/results-analysis/follow-up', { params }),
  getTopAbnormalTests: (params) => api.get('/results-analysis/top-abnormal-tests', { params }),
  getTrend: (params) => api.get('/results-analysis/trend', { params }),
  getDemographics: (params) => api.get('/results-analysis/demographics', { params }),
  getPatientProfile: (customerId) => api.get(`/results-analysis/patient/${customerId}`),
};

// Lab Chat API
export const chatAPI = {
  getEmployees: (params) => api.get('/chat/employees', { params }),
  getThreads: () => api.get('/chat/threads'),
  createThread: (data) => api.post('/chat/threads', data),
  getThreadMessages: (threadId, params) => api.get(`/chat/threads/${threadId}/messages`, { params }),
  sendMessage: (threadId, data) => api.post(`/chat/threads/${threadId}/messages`, data),
  markThreadRead: (threadId) => api.post(`/chat/threads/${threadId}/read`),
  getUnreadSummary: (params) => api.get('/chat/unread-summary', { params }),
};

// ─── Super Admin API ───
export const superAdminAPI = {
  login: (data) => axios.post(`${API_BASE_URL}/super-admin/auth/login`, data),
  getProfile: () => superAdminAxios.get('/super-admin/auth/me'),
  getDashboard: () => superAdminAxios.get('/super-admin/dashboard'),
  // Revenue & Analytics
  getRevenue: (params) => superAdminAxios.get('/super-admin/analytics/revenue', { params }),
  getTenantAnalytics: (params) => superAdminAxios.get('/super-admin/analytics/tenants', { params }),
  getUsageAnalytics: () => superAdminAxios.get('/super-admin/analytics/usage'),
  // Tenants
  getTenants: (params) => superAdminAxios.get('/super-admin/tenants', { params }),
  getTenant: (id) => superAdminAxios.get(`/super-admin/tenants/${id}`),
  createTenant: (data) => superAdminAxios.post('/super-admin/tenants', data),
  updateTenant: (id, data) => superAdminAxios.put(`/super-admin/tenants/${id}`, data),
  toggleTenantStatus: (id) => superAdminAxios.patch(`/super-admin/tenants/${id}/toggle-status`),
  getTenantUsage: (id) => superAdminAxios.get(`/super-admin/tenants/${id}/usage`),
  // Plans
  getPlans: () => superAdminAxios.get('/super-admin/plans'),
  createPlan: (data) => superAdminAxios.post('/super-admin/plans', data),
  updatePlan: (id, data) => superAdminAxios.put(`/super-admin/plans/${id}`, data),
  deletePlan: (id) => superAdminAxios.delete(`/super-admin/plans/${id}`),
  duplicatePlan: (id, data) => superAdminAxios.post(`/super-admin/plans/${id}/duplicate`, data),
  // Features
  getFeatures: () => superAdminAxios.get('/super-admin/features'),
  seedFeatures: () => superAdminAxios.post('/super-admin/features/seed'),
  createFeature: (data) => superAdminAxios.post('/super-admin/features', data),
  updateFeature: (id, data) => superAdminAxios.put(`/super-admin/features/${id}`, data),
  deleteFeature: (id) => superAdminAxios.delete(`/super-admin/features/${id}`),
  // Subscriptions
  getSubscriptions: (params) => superAdminAxios.get('/super-admin/subscriptions', { params }),
  updateSubscription: (id, data) => superAdminAxios.put(`/super-admin/subscriptions/${id}`, data),
  extendSubscription: (id, data) => superAdminAxios.patch(`/super-admin/subscriptions/${id}/extend`, data),
  suspendSubscription: (id) => superAdminAxios.patch(`/super-admin/subscriptions/${id}/suspend`),
  activateSubscription: (id) => superAdminAxios.patch(`/super-admin/subscriptions/${id}/activate`),
  // Feature overrides
  setFeatureOverride: (subId, data) => superAdminAxios.post(`/super-admin/subscriptions/${subId}/overrides`, data),
  removeFeatureOverride: (subId, key) => superAdminAxios.delete(`/super-admin/subscriptions/${subId}/overrides/${key}`),
  // Payments
  getPayments: (params) => superAdminAxios.get('/super-admin/payments', { params }),
  recordPayment: (data) => superAdminAxios.post('/super-admin/payments', data),
  // Impersonation
  impersonate: (tenantId) => superAdminAxios.post(`/super-admin/tenants/${tenantId}/impersonate`),
  // Audit
  getAuditLog: (params) => superAdminAxios.get('/super-admin/audit-log', { params }),
  // ZATCA
  getZatcaOverview: () => superAdminAxios.get('/super-admin/zatca/overview'),
  // Admins
  getAdmins: () => superAdminAxios.get('/super-admin/admins'),
  createAdmin: (data) => superAdminAxios.post('/super-admin/admins', data),
  updateAdmin: (id, data) => superAdminAxios.put(`/super-admin/admins/${id}`, data),
  deleteAdmin: (id) => superAdminAxios.delete(`/super-admin/admins/${id}`),
  // Lab Test Templates
  getTemplates: () => superAdminAxios.get('/super-admin/templates'),
  getTemplate: (id) => superAdminAxios.get(`/super-admin/templates/${id}`),
  getTemplateTests: (id, params) => superAdminAxios.get(`/super-admin/templates/${id}/tests`, { params }),
  seedTemplates: () => superAdminAxios.post('/super-admin/templates/seed'),
  updateTemplate: (id, data) => superAdminAxios.put(`/super-admin/templates/${id}`, data),
  updateTemplateTest: (id, data) => superAdminAxios.put(`/super-admin/templates/${id}/test`, data),
  updateTemplateComponent: (id, data) => superAdminAxios.put(`/super-admin/templates/${id}/component`, data),
  duplicateTemplate: (id) => superAdminAxios.post(`/super-admin/templates/${id}/duplicate`),
};

// ─── Onboarding API ───
export const onboardingAPI = {
  register: (data) => api.post('/onboarding/register', data),
  checkSlug: (slug) => api.get(`/onboarding/check-slug/${slug}`),
  updateProfile: (data) => api.put('/onboarding/profile', data),
  updateBranding: (data) => api.put('/onboarding/branding', data),
  createBranches: (data) => api.post('/onboarding/branches', data),
  createStaff: (data) => api.post('/onboarding/staff', data),
  getTemplates: () => api.get('/onboarding/templates'),
  importTests: (templateId) => api.post('/onboarding/import-tests', { templateId }),
  complete: () => api.post('/onboarding/complete'),
  getProgress: () => api.get('/onboarding/progress'),
  getStatus: () => api.get('/onboarding/status'),
};

// ─── Payment Orders API (customer) ───
export const paymentOrdersAPI = {
  getMyActive: () => api.get('/payment-orders/my-active'),
  updateDraft: (data) => api.post('/payment-orders/draft', data),
  submit: () => api.post('/payment-orders/submit'),
  // Setting Content-Type to undefined forces axios to drop the default
  // application/json header on this axios instance and regenerate the
  // header from the FormData (multipart/form-data; boundary=...) so multer
  // can actually parse the file field. Without this, the inherited
  // application/json overrides FormData's own content type and the server
  // sees an empty body.
  uploadReceipt: (orderId, formData) => api.post(
    `/payment-orders/${orderId}/receipt`,
    formData,
    { headers: { 'Content-Type': undefined } },
  ),
  cancelDraft: () => api.post('/payment-orders/cancel-draft'),
};

// ─── Admin Payment-Order Approvals ───
// These calls go through the super-admin JWT stored separately from the
// tenant one — the SubscriptionsList page already uses this pattern.
export const adminPaymentOrdersAPI = {
  list: (params = {}) => superAdminAxios.get('/super-admin/payment-orders', { params }),
  getDetail: (id) => superAdminAxios.get(`/super-admin/payment-orders/${id}`),
  approve: (id) => superAdminAxios.post(`/super-admin/payment-orders/${id}/approve`),
  reject: (id, reason) => superAdminAxios.post(`/super-admin/payment-orders/${id}/reject`, { reason }),
  requestNewReceipt: (id) => superAdminAxios.post(`/super-admin/payment-orders/${id}/request-new-receipt`),
};

// ─── Admin Discount Codes ───
export const adminDiscountCodesAPI = {
  list: (params = {}) => superAdminAxios.get('/super-admin/discount-codes', { params }),
  getById: (id) => superAdminAxios.get(`/super-admin/discount-codes/${id}`),
  create: (data) => superAdminAxios.post('/super-admin/discount-codes', data),
  update: (id, data) => superAdminAxios.patch(`/super-admin/discount-codes/${id}`, data),
  toggle: (id, isActive) => superAdminAxios.patch(`/super-admin/discount-codes/${id}/toggle`, isActive !== undefined ? { isActive } : {}),
  delete: (id) => superAdminAxios.delete(`/super-admin/discount-codes/${id}`),
};

// ─── Subscription API (for tenant users) ───
export const subscriptionAPI = {
  getMy: () => api.get('/subscriptions/my'),
  getPlans: () => api.get('/subscriptions/plans'),
  upgrade: (data) => api.post('/subscriptions/upgrade', data),
  addBranch: () => api.post('/subscriptions/add-branch'),
  addDevice: () => api.post('/subscriptions/add-device'),
  renew: (data) => api.post('/subscriptions/renew', data),
  calculatePrice: (data) => api.post('/subscriptions/calculate-price', data),
  getUsage: () => api.get('/subscriptions/usage'),
  getPayments: (params) => api.get('/subscriptions/payments', { params }),
  createCharge: (data) => api.post('/payments/create-charge', data),
  verifyPayment: (data) => api.post('/payments/verify', data),
  // Add-ons (Phase 1)
  getAddons: () => api.get('/subscriptions/addons'),
  purchaseCustomDomain: (data) => api.post('/subscriptions/addons/custom-domain/purchase', data),
  cancelAddon: (addonId) => api.post(`/subscriptions/addons/${addonId}/cancel`),
};

// ─── Per-Tenant Marketing Website ───
export const websiteAPI = {
  // Site-wide settings (header/footer/theme/SEO/analytics) — Phase 3
  getSiteSettings: () => api.get('/website/site-settings'),
  saveSiteSettingsDraft: (data) => api.put('/website/site-settings/draft', data),
  publishSiteSettings: () => api.post('/website/site-settings/publish'),
  revertSiteSettings: () => api.post('/website/site-settings/revert'),
  siteSettingsPreviewToken: () => api.post('/website/site-settings/preview-token'),

  // Content manifest — template-aware. Pass the tenant's current template
  // slug so the editor form matches the live site's defaults exactly.
  contentManifest: (templateSlug) => api.get('/website/content-manifest', {
    params: templateSlug ? { template: templateSlug } : undefined,
  }),
  initialize: () => api.post('/website/initialize'),
  listPages: () => api.get('/website/pages'),
  getPage: (key) => api.get(`/website/pages/${key}`),
  savePageDraft: (key, data) => api.put(`/website/pages/${key}/draft`, data),
  publishPage: (key) => api.post(`/website/pages/${key}/publish`),
  revertPage: (key) => api.post(`/website/pages/${key}/revert`),
  pagePreviewToken: (key) => api.post(`/website/pages/${key}/preview-token`),

  // Template gallery + onboarding wizard
  listTemplates: () => api.get('/website/templates'),
  getTemplate: (slug) => api.get(`/website/templates/${slug}`),
  selectTemplate: (slug) => api.post('/website/select-template', { slug }),
  completeOnboarding: (data) => api.post('/website/complete-onboarding', data),

  // Block library catalog — JSON list of every drag-into-page block type
  // (categories + per-block schemas). Phase 2/3 — used by the BlockPickerModal.
  blockLibrary: () => api.get('/website/block-library'),

  // Version history (Phase 5) — list past publishes + restore to draft.
  pageHistory: (key) => api.get(`/website/pages/${key}/history`),
  restorePageHistory: (key, index) => api.post(`/website/pages/${key}/history/restore`, { index }),
};

export const blogAPI = {
  // Posts
  list: (params) => api.get('/blog/posts', { params }),
  get: (id) => api.get(`/blog/posts/${id}`),
  create: (data) => api.post('/blog/posts', data),
  update: (id, data) => api.patch(`/blog/posts/${id}`, data),
  publish: (id) => api.post(`/blog/posts/${id}/publish`),
  archive: (id) => api.post(`/blog/posts/${id}/archive`),
  remove: (id) => api.delete(`/blog/posts/${id}`),
  // Categories
  listCategories: () => api.get('/blog/categories'),
  createCategory: (data) => api.post('/blog/categories', data),
  updateCategory: (id, data) => api.patch(`/blog/categories/${id}`, data),
  removeCategory: (id) => api.delete(`/blog/categories/${id}`),
};

export const offersAPI = {
  list: (params) => api.get('/offers', { params }),
  get: (id) => api.get(`/offers/${id}`),
  create: (data) => api.post('/offers', data),
  update: (id, data) => api.patch(`/offers/${id}`, data),
  publish: (id) => api.post(`/offers/${id}/publish`),
  archive: (id) => api.post(`/offers/${id}/archive`),
  remove: (id) => api.delete(`/offers/${id}`),
};

export const mediaAPI = {
  list: (params) => api.get('/media', { params }),
  usage: () => api.get('/media/usage'),
  get: (id) => api.get(`/media/${id}`),
  getSignedUrl: (data) => api.post('/media/signed-upload-url', data),
  confirmUpload: (data) => api.post('/media/confirm-upload', data),
  update: (id, data) => api.patch(`/media/${id}`, data),
  remove: (id, { soft = false } = {}) => api.delete(`/media/${id}${soft ? '?soft=true' : ''}`),
};

export const domainsAPI = {
  list: () => api.get('/domains'),
  add: (domain) => api.post('/domains', { domain }),
  verify: (id) => api.post(`/domains/${id}/verify`),
  remove: (id) => api.delete(`/domains/${id}`),
};

// ─── Public API (no auth) ───
export const publicAPI = {
  getBranding: (slug) => axios.get(`${API_BASE_URL}/public/tenants/branding/${slug}`),
  getContractorBranding: (email) => axios.get(`${API_BASE_URL}/public/tenants/contractor-branding`, { params: { email } }),
  getPlans: () => axios.get(`${API_BASE_URL}/public/plans`),
  getStats: () => axios.get(`${API_BASE_URL}/public/tenants/stats`),
};

export default api;

